/**
 * BulkActionToolbar Component
 * 
 * A sticky toolbar that appears when items are selected.
 * Provides configurable bulk actions and selection management.
 */

import { useState, useCallback } from 'react';
import { X, CheckSquare, Loader2 } from 'lucide-react';
import type { BulkActionToolbarProps, BulkAction, BulkActionVariant } from './types';

// ============================================================================
// Confirmation Dialog
// ============================================================================

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  selectedCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: BulkActionVariant;
}

function ConfirmDialog({
  isOpen,
  title,
  message,
  selectedCount,
  onConfirm,
  onCancel,
  variant = 'primary'
}: ConfirmDialogProps) {
  if (!isOpen) return null;
  
  const confirmButtonClasses = {
    primary: 'btn-primary',
    secondary: 'bg-[var(--surface)] hover:bg-[var(--bg-raised)] text-white',
    danger: 'bg-[var(--coral)] hover:bg-[var(--coral)] text-white',
    ghost: 'bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] text-[var(--text-sec)]',
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-[var(--bg-raised)] shadow-2xl border border-granite-gray-200 max-w-md w-full mx-4 overflow-hidden">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-granite-gray-900">{title}</h3>
          <p className="mt-2 text-granite-gray-600">{message}</p>
          <p className="mt-1 text-sm text-granite-gray-500">This will affect {selectedCount} items.</p>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-granite-gray-50">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium  ${confirmButtonClasses[variant]}`}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Action Button Component
// ============================================================================

interface ActionButtonProps {
  action: BulkAction;
  selectedIds: string[];
  onExecute: (action: BulkAction) => void;
  isLoading?: boolean;
}

function ActionButton({ action, selectedIds, onExecute, isLoading }: ActionButtonProps) {
  const isDisabled = typeof action.disabled === 'function' 
    ? action.disabled(selectedIds) 
    : action.disabled;
  
  const variantClasses: Record<BulkActionVariant, string> = {
    primary: 'text-white bg-electric-mustard-600 hover:bg-electric-mustard-700',
    secondary: 'text-white bg-granite-gray-700 hover:bg-granite-gray-600',
    danger: 'text-luminous-coral-400 bg-transparent hover:text-white hover:bg-luminous-coral-700',
    ghost: 'text-granite-gray-300 hover:text-white hover:bg-granite-gray-800',
  };
  
  const Icon = action.icon;
  const variant = action.variant || 'secondary';
  
  return (
    <button
      type="button"
      onClick={() => onExecute(action)}
      disabled={isDisabled || isLoading}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium 
                   transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                  ${variantClasses[variant]}`}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : Icon ? (
        <Icon className="h-4 w-4" />
      ) : null}
      <span>{action.label}</span>
    </button>
  );
}

// ============================================================================
// BulkActionToolbar Component
// ============================================================================

export function BulkActionToolbar({
  selectedCount,
  totalCount,
  actions,
  onSelectAll,
  onClearSelection,
  className = '',
}: BulkActionToolbarProps) {
  const [confirmAction, setConfirmAction] = useState<BulkAction | null>(null);
  const [loadingActionId, setLoadingActionId] = useState<string | null>(null);
  const [selectedIds] = useState<string[]>([]);
  
  const handleExecuteAction = useCallback(async (action: BulkAction) => {
    if (action.confirmMessage) {
      setConfirmAction(action);
      return;
    }
    
    setLoadingActionId(action.id);
    try {
      await action.onClick(selectedIds);
    } finally {
      setLoadingActionId(null);
    }
  }, [selectedIds]);
  
  const handleConfirm = useCallback(async () => {
    if (!confirmAction) return;
    
    setLoadingActionId(confirmAction.id);
    setConfirmAction(null);
    
    try {
      await confirmAction.onClick(selectedIds);
    } finally {
      setLoadingActionId(null);
    }
  }, [confirmAction, selectedIds]);
  
  const handleCancel = useCallback(() => {
    setConfirmAction(null);
  }, []);
  
  // Don't show toolbar if nothing is selected
  if (selectedCount === 0) {
    return null;
  }
  
  const isAllSelected = selectedCount === totalCount;
  
  return (
    <>
      <div 
        className={`sticky bottom-4 mx-auto max-w-fit z-40 ${className}`}
      >
        <div className="flex items-center gap-4 px-4 py-3 bg-granite-gray-900 border border-granite-gray-700 shadow-[0_8px_32px_rgba(0,0,0,0.35)] backdrop-blur-sm animate-slide-up text-white">
          {/* Selection info */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClearSelection}
              className="p-1 rounded hover:bg-granite-gray-700 transition-colors"
              title="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
            
            <span className="text-sm font-medium">
              {selectedCount} selected
            </span>
            
            {!isAllSelected && (
              <button
                type="button"
                onClick={onSelectAll}
                className="flex items-center gap-1 text-sm text-granite-gray-300 hover:text-white transition-colors"
              >
                <CheckSquare className="h-4 w-4" />
                Select all {totalCount}
              </button>
            )}
          </div>
          
          {/* Divider */}
          <div className="w-px h-6 bg-granite-gray-700" />
          
          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {actions.map((action) => (
              <ActionButton
                key={action.id}
                action={action}
                selectedIds={selectedIds}
                onExecute={handleExecuteAction}
                isLoading={loadingActionId === action.id}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Confirmation dialog */}
      <ConfirmDialog
        isOpen={!!confirmAction}
        title="Confirm Action"
        message={confirmAction?.confirmMessage || ''}
        selectedCount={selectedCount}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        variant={confirmAction?.variant}
      />
    </>
  );
}

// ============================================================================
// Inline variant for use within content area
// ============================================================================

interface InlineBulkActionsProps {
  selectedCount: number;
  totalCount: number;
  actions: BulkAction[];
  selectedIds: string[];
  onSelectAll: () => void;
  onClearSelection: () => void;
}

export function InlineBulkActions({
  selectedCount,
  totalCount,
  actions,
  selectedIds,
  onSelectAll,
  onClearSelection,
}: InlineBulkActionsProps) {
  const [loadingActionId, setLoadingActionId] = useState<string | null>(null);
  
  if (selectedCount === 0) return null;
  
  return (
    <div className="flex items-center justify-between px-4 py-2 bg-primary-50 border border-primary-200">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-primary-800">
          {selectedCount} of {totalCount} selected
        </span>
        
        <button
          type="button"
          onClick={onClearSelection}
          className="text-sm text-primary-600 hover:text-primary-800 underline"
        >
          Clear
        </button>
        
        {selectedCount < totalCount && (
          <button
            type="button"
            onClick={onSelectAll}
            className="text-sm text-primary-600 hover:text-primary-800 underline"
          >
            Select all
          </button>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        {actions.map((action) => {
          const Icon = action.icon;
          const isDisabled = typeof action.disabled === 'function'
            ? action.disabled(selectedIds)
            : action.disabled;
          
          return (
            <button
              key={action.id}
              type="button"
              onClick={async () => {
                setLoadingActionId(action.id);
                try {
                  await action.onClick(selectedIds);
                } finally {
                  setLoadingActionId(null);
                }
              }}
              disabled={isDisabled || loadingActionId === action.id}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium 
                           transition-colors disabled:opacity-50
                          ${action.variant === 'danger' 
                            ? 'text-[var(--coral)] hover:bg-[var(--coral-dim)]' 
                            : 'text-primary-700 hover:bg-primary-100'
                          }`}
            >
              {loadingActionId === action.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : Icon ? (
                <Icon className="h-4 w-4" />
              ) : null}
              {action.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default BulkActionToolbar;
